import {
  Cart,
  Share,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import {
  ChatSolid,
  SaleTagSolid,
} from "@lazarentertainment/web-design-system/components/iconSolid"
import {
  ExpandableTruncate,
  Truncate,
} from "@lazarentertainment/web-design-system/components/truncate"
import { Button } from "client/designSystem/Button"
import {
  Tab,
  TabContent,
  TabsList,
  TabsRoot,
} from "client/designSystem/Tabs"
import Chat from "client/packages/chat"
import { useCartContext } from "client/packages/commerce/components/cart/CartContext"
import CommerceFlow from "client/packages/commerce/components/CommerceFlow"
import {
  CommerceView,
  useShopContext,
} from "client/packages/commerce/components/ShopContext"
import { CommerceHeader } from "client/packages/commerce/components/ui/CommerceHeader"
import {
  SidePanelTab,
  useGlobalContext,
} from "client/packages/utils/GlobalContext"
import { ShareDialog } from "client/views/ShareView/ShareDialog"

import { useChannelContext } from "../channel"
import { UserAvatarImage } from "../channel/UserAvatarImage"

import styles from "./sidePanelContent.module.css"

export const SidePanelContent = () => {
  const {
    shop,
    view: commerceView,
    setView: setCommerceView,
    resetView,
    currentlySelectedProductId,
    setAddedProduct,
  } = useShopContext()
  const {
    broadcastTitle,
    broadcastDescription,
    recording,
    title: channelTitle,
  } = useChannelContext()
  const {
    state: {
      isChatPluginAvailable,
      isCommercePluginAvailable,
      sidePanelSelectedTab,
    },
    setSidePanelSelectedTab,
  } = useGlobalContext()
  const { cart } = useCartContext()

  const commerceTakesFullSidePanel =
    sidePanelSelectedTab === SidePanelTab.COMMERCE &&
    (currentlySelectedProductId || commerceView === CommerceView.CART)

  if (commerceTakesFullSidePanel) {
    return (
      <div className={styles.sidePanelContent}>
        <CommerceHeader />
        <CommerceFlow shop={shop} />
      </div>
    )
  }

  const title = recording ? recording.title : broadcastTitle
  const description = recording
    ? recording.description
    : broadcastDescription

  const numCartItems = cart?.totalQuantity ?? 0

  return (
    <TabsRoot
      className={styles.sidePanelContent}
      value={sidePanelSelectedTab}
      onValueChange={(value) => {
        if (value === SidePanelTab.CHAT) {
          setSidePanelSelectedTab(SidePanelTab.CHAT)
        }
        if (value === SidePanelTab.COMMERCE) {
          resetView()
          setSidePanelSelectedTab(SidePanelTab.COMMERCE)
        }
      }}
    >
      {(title || description) && (
        <div className={styles.sidePanelHeader}>
          <div className={styles.sidePanelTitleRow}>
            <div className={styles.sidePanelTitleColumn}>
              <p
                className={`${styles.channelTitleRow} body-md text-color-tertiary`}
              >
                <UserAvatarImage size="xs" />
                {channelTitle}
              </p>
              {title && (
                <h1 className={`${styles.streamTitle} title-lg`}>
                  <Truncate lineCount={2}>{title}</Truncate>
                </h1>
              )}
            </div>
            <ShareDialog
              trigger={
                <Button
                  variant="outline"
                  size="md"
                  icon={Share}
                  iconOnly
                  aria-label="Share"
                />
              }
            />
          </div>
          {description && (
            <ExpandableTruncate
              lineCount={1}
              containerElement="p"
              className={`${styles.streamDescription} body-md`}
            >
              {description}
            </ExpandableTruncate>
          )}
        </div>
      )}

      <div className={styles.sidePanelTabControlsRow}>
        <TabsList>
          {isChatPluginAvailable && (
            <Tab
              value={SidePanelTab.CHAT}
              icon={ChatSolid}
              data-robot="chat-tab"
            >
              Chat
            </Tab>
          )}
          {isCommercePluginAvailable && (
            <Tab
              variant="rose"
              value={SidePanelTab.COMMERCE}
              icon={SaleTagSolid}
              data-robot="commerce-tab"
              onClick={() => {
                if (sidePanelSelectedTab === SidePanelTab.COMMERCE) {
                  resetView()
                }
              }}
            >
              Shop
            </Tab>
          )}
        </TabsList>

        {numCartItems > 0 && (
          <Button
            variant="secondary"
            notificationDotText={numCartItems.toString()}
            icon={Cart}
            onClick={() => {
              setSidePanelSelectedTab(SidePanelTab.COMMERCE)
              setCommerceView(CommerceView.CART)
              setAddedProduct(null)
            }}
            data-robot="cart-open"
          >
            Cart
          </Button>
        )}
      </div>
      <TabContent
        value={SidePanelTab.CHAT}
        className={styles.sidePanelTabContent}
      >
        <Chat />
      </TabContent>
      <TabContent
        value={SidePanelTab.COMMERCE}
        className={styles.sidePanelTabContent}
      >
        <CommerceFlow shop={shop} />
      </TabContent>
    </TabsRoot>
  )
}
